import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search } from '@sparkpost/matchbox-icons';
import {
  listPaginated,
  changeSubaccountsPage,
  resetPaginatedSubaccounts
} from 'src/actions/subaccounts';
import { ApiErrorBanner } from 'src/components';
import CursorPaging from 'src/components/collection/CursorPaging';
import PerPageButtons from 'src/components/collection/PerPageButtons';
import TableHeader from 'src/components/collection/TableHeader';
import { Users } from 'src/components/images';
import { PageLink } from 'src/components/links';
import { Box, Page, Panel, Select, Table, Tag, TextField, Button } from 'src/components/matchbox';
import { CREATE_SUBACCOUNT, LINKS, ROLES } from 'src/constants';
import { useHibana } from 'src/context/HibanaContext';
import { hasRole } from 'src/helpers/conditions/user';
import { onEnter } from 'src/helpers/keyEvents';
import { snakeToFriendly } from 'src/helpers/string';
import { selectPaginatedSubaccounts } from 'src/selectors/subaccounts';
import InfoBanner from './components/InfoBanner';
import SubaccountEmptyState from './components/SubaccountEmptyState';
import { statusTagColors } from './helpers/getRowData';

const columns = [
  { label: 'Name', width: '40%', sortKey: 'name' },
  { label: 'ID', width: '20%', sortKey: 'id' },
  { label: 'Status', width: '20%' }
];

const primaryAction = {
  content: 'Create Subaccount',
  Component: PageLink,
  to: CREATE_SUBACCOUNT
};

const DEFAULT_PER_PAGE = 25;

const statusFilterOptions = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'terminated', label: 'Terminated' }
];

function ListPage() {
  const [{ isHibanaEnabled }] = useHibana();
  const dispatch = useDispatch();

  const [currentPage, setCurrentPage] = useState(0);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [nameFilter, setNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isFirstRender, setIsFirstRender] = useState(true);

  const subaccounts = useSelector(selectPaginatedSubaccounts);
  const loading = useSelector((state) => state.subaccountsPaginated.loading);
  const error = useSelector((state) => state.subaccountsPaginated.error);
  const totalCount = useSelector((state) => state.subaccountsPaginated.totalCount);
  const linkByPage = useSelector((state) => state.subaccountsPaginated.linkByPage);

  const hasNextPage = Boolean(linkByPage[currentPage + 1]);

  const isOnlyViewUser = useSelector(
    (state) => hasRole(ROLES.REPORTING)(state) || hasRole(ROLES.TEMPLATES)(state)
  );

  const fetchSubaccounts = useCallback(
    ({ page = 0, perPage: pp, sortBy: sb, order: so, name, status } = {}) => {
      dispatch(resetPaginatedSubaccounts());
      setCurrentPage(page);
      dispatch(
        listPaginated({
          perPage: pp,
          sortBy: sb,
          order: so,
          name: name || undefined,
          status: status || undefined
        })
      );
    },
    [dispatch]
  );

  // Initial fetch
  useEffect(() => {
    setIsFirstRender(false);
    fetchSubaccounts({ perPage, sortBy, order: sortOrder, name: nameFilter, status: statusFilter });
    return () => {
      dispatch(resetPaginatedSubaccounts());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSortChange = useCallback(
    (newSortColumn, newSortDirection) => {
      setSortBy(newSortColumn);
      setSortOrder(newSortDirection);
      fetchSubaccounts({
        perPage,
        sortBy: newSortColumn,
        order: newSortDirection,
        name: nameFilter,
        status: statusFilter
      });
    },
    [fetchSubaccounts, perPage, nameFilter, statusFilter]
  );

  const handleApplyFilter = useCallback(() => {
    fetchSubaccounts({ perPage, sortBy, order: sortOrder, name: nameFilter, status: statusFilter });
  }, [fetchSubaccounts, perPage, sortBy, sortOrder, nameFilter, statusFilter]);

  const handleFilterOnChange = useCallback((e) => {
    setNameFilter(e.target.value);
  }, []);

  const handleStatusFilterChange = useCallback(
    (e) => {
      const newStatus = e.target.value;
      setStatusFilter(newStatus);
      fetchSubaccounts({ perPage, sortBy, order: sortOrder, name: nameFilter, status: newStatus });
    },
    [fetchSubaccounts, perPage, sortBy, sortOrder, nameFilter]
  );

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page - 1);
      dispatch(changeSubaccountsPage(page));
    },
    [dispatch]
  );

  const handleFirstPage = useCallback(() => {
    fetchSubaccounts({ perPage, sortBy, order: sortOrder, name: nameFilter, status: statusFilter });
  }, [fetchSubaccounts, perPage, sortBy, sortOrder, nameFilter, statusFilter]);

  const handleLoadingError = useCallback(() => {
    setNameFilter('');
    setStatusFilter('');
    fetchSubaccounts({ perPage, sortBy, order: sortOrder, name: '', status: '' });
  }, [fetchSubaccounts, perPage, sortBy, sortOrder]);

  const handlePerPageChange = useCallback(
    (newPerPage) => {
      setPerPage(newPerPage);
      fetchSubaccounts({
        perPage: newPerPage,
        sortBy,
        order: sortOrder,
        name: nameFilter,
        status: statusFilter
      });
    },
    [fetchSubaccounts, sortBy, sortOrder, nameFilter, statusFilter]
  );

  const getRowData = useCallback(
    ({ status, id, name }) => [
      isOnlyViewUser ? (
        <span>{name}</span>
      ) : (
        <PageLink to={`/account/subaccounts/${id}`}>{name}</PageLink>
      ),
      <p>{id}</p>,
      <Tag color={statusTagColors[status]}>{snakeToFriendly(status)}</Tag>
    ],
    [isOnlyViewUser]
  );

  const EmptyStateComponent = useCallback(
    () => <SubaccountEmptyState isOnlyViewUser={isOnlyViewUser} />,
    [isOnlyViewUser]
  );

  const renderError = () => (
    <ApiErrorBanner
      errorDetails={error.message}
      message="Sorry, we ran into an error loading your Subaccounts"
      reload={handleLoadingError}
    />
  );

  const renderCollection = () => (
    <>
      <Panel.LEGACY sectioned>
        <Box display="flex" alignItems="end">
          <Box flex="1" mr="300">
            <TextField
              labelHidden
              label="Filter By"
              id="subaccounts-filter-box"
              data-id="subaccounts-filter-box"
              name="subaccounts-filter-box"
              prefix={<Search />}
              placeholder="Filter by name"
              value={nameFilter}
              onChange={handleFilterOnChange}
              onKeyDown={onEnter(handleApplyFilter)}
              data-track={true}
              connectRight={
                <Button variant="connected" useMatchboxVariant={false} onClick={handleApplyFilter}>
                  Filter
                </Button>
              }
            />
          </Box>
          <Box width="200px">
            <Select
              id="subaccounts-status-filter"
              label="Filter by status"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              options={statusFilterOptions}
            />
          </Box>
        </Box>
      </Panel.LEGACY>
      <Panel.LEGACY mb="500">
        <div>
          <Table>
            <TableHeader
              columns={columns}
              onSort={handleSortChange}
              sortColumn={sortBy}
              sortDirection={sortOrder}
            />
            <tbody>
              {subaccounts.length === 0 && !loading ? (
                <Table.Row>
                  <Table.Cell colSpan={3}>
                    <Box padding="400" textAlign="center">
                      No results found.
                    </Box>
                  </Table.Cell>
                </Table.Row>
              ) : (
                subaccounts.map((row) => <Table.Row key={row.id} rowData={getRowData(row)} />)
              )}
            </tbody>
          </Table>
        </div>
      </Panel.LEGACY>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <CursorPaging
          currentPage={currentPage + 1}
          handlePageChange={handlePageChange}
          previousDisabled={currentPage === 0 || loading}
          nextDisabled={!hasNextPage || loading}
          handleFirstPage={handleFirstPage}
          perPage={perPage}
          totalCount={totalCount}
        />
        <PerPageButtons
          totalCount={totalCount}
          onPerPageChange={handlePerPageChange}
          perPage={perPage}
          perPageButtons={[10, 25, 50, 100]}
        />
      </Box>
    </>
  );

  return (
    <Page
      title="Subaccounts"
      primaryAction={primaryAction}
      empty={{
        show: totalCount === 0 && !loading && !nameFilter && !statusFilter,
        title: 'Manage your subaccounts',
        image: Users,
        content: <p>Subaccounts are a good way of managing external client accounts.</p>,
        secondaryAction: {
          content: 'Learn more',
          to: LINKS.SUBACCOUNTS_API,
          external: true
        }
      }}
      emptyStateComponent={EmptyStateComponent}
      loading={(loading && subaccounts.length === 0) || isFirstRender}
    >
      {isHibanaEnabled && <InfoBanner />}
      {error ? renderError() : renderCollection()}
    </Page>
  );
}

export default ListPage;
